/**
 * The two bars that appear when the server capped the graph —
 * ROUTE_INVENTORY §2.10 #6.
 *
 * The backend ranks by importance + recency + FSRS attention and returns the
 * top 300. Past a few thousand nodes the force simulation stops being usable,
 * so the cap is a performance floor rather than a data limit — which is why
 * the override exists and why it is worded as a warning rather than a setting.
 *
 * Drawn by `PageBanner` (`layout='inline'`, no dismiss, no live region), so
 * it speaks the same visual language as the page-wide strips: a neutral card
 * surface, and tone only in the icon and the 3px left accent. It used to be a
 * full amber tint with an amber-bordered button, and its ranking sub-label on
 * that tint measured 3.42:1 at `/80` alpha before it was fixed; on the neutral
 * surface it is ordinary muted text. Subordinate by colour role, never by
 * alpha.
 *
 * They are mutually exclusive and they read as a pair: `GraphCapBanner` is the
 * warning-toned "you are not seeing everything, want the rest?" state, and
 * `GraphCapOverrideBar` is the info-toned "you asked for everything, here is
 * the way back" state. Warning for the first because it is information the
 * user did not ask for; info for the second because it is the consequence of
 * a choice they just made.
 *
 * Desktop only: a phone says the same in the header's count line ("Top 300 of
 * 1,247 · Show all"), see `ArchivePhoneHeader`.
 */

import { Gauge, Layers } from 'lucide-react';

import { PageBanner } from '@/components/core/common/banners/PageBanner';

interface GraphCapBannerProps {
  /** Nodes actually rendered — the capped count. */
  visible: number;
  /** Total the server says exist. */
  total: number | null;
  /** Lift the cap to `NODE_CAP_OVERRIDE`. */
  onShowAll: () => void;
}

export function GraphCapBanner({
  visible,
  total,
  onShowAll,
}: GraphCapBannerProps) {
  return (
    <PageBanner
      layout='inline'
      tone='warning'
      icon={Layers}
      message={`Showing ${visible} of ${total?.toLocaleString() ?? '?'} concepts.`}
      detail='Ranked by importance + recency + FSRS attention.'
      action={{ label: 'Show all', onClick: onShowAll }}
    />
  );
}

interface GraphCapOverrideBarProps {
  total: number | null;
  /** The cap that would be restored — shown in the button label. */
  defaultCap: number;
  onRestoreCap: () => void;
}

export function GraphCapOverrideBar({
  total,
  defaultCap,
  onRestoreCap,
}: GraphCapOverrideBarProps) {
  return (
    <PageBanner
      layout='inline'
      tone='info'
      icon={Gauge}
      message={`Showing all ${total?.toLocaleString()} concepts.`}
      detail='The graph may be slow.'
      action={{ label: `Back to top ${defaultCap}`, onClick: onRestoreCap }}
    />
  );
}
